import { TreeViewBaseItem } from '@mui/x-tree-view';
// eslint-disable-next-line no-restricted-imports
import { UseTreeViewItemsPublicAPI } from '@mui/x-tree-view/internals/plugins/useTreeViewItems/useTreeViewItems.types';
import { TreeItemData } from './types';

export function getItemChildValues(
  item: TreeViewBaseItem<TreeItemData>
): string[] {
  const ids: string[] = [];

  if (item.children) {
    for (const child of item.children) {
      ids.push(child.value, ...getItemChildValues(child));
    }
  }

  return ids;
}

export function getItemParentIds(
  item: TreeViewBaseItem<TreeItemData>,
  api: UseTreeViewItemsPublicAPI<TreeViewBaseItem<TreeItemData>>
): string[] {
  const ids: string[] = [];
  const stack: TreeViewBaseItem<TreeItemData>[] = [item];

  while (stack.length > 0) {
    const currentItem = stack.pop()!;

    if (currentItem.parentId) {
      ids.push(currentItem.parentId);
      const parent = api.getItem(currentItem.parentId);
      if (parent) stack.push(parent);
    }
  }

  return ids;
}

export function getItemParentValues(
  item: TreeViewBaseItem<TreeItemData>,
  api: UseTreeViewItemsPublicAPI<TreeViewBaseItem<TreeItemData>>
): string[] {
  const values: string[] = [];
  const stack: TreeViewBaseItem<TreeItemData>[] = [item];

  while (stack.length > 0) {
    const currentItem = stack.pop()!;

    if (currentItem?.parentId && currentItem?.parentValue) {
      values.push(currentItem.parentValue);
      const parent = api.getItem(currentItem.parentId);
      if (parent) stack.push(parent);
    }
  }

  return values;
}

export function getAllTreeItemIdsIterative(
  items?: TreeViewBaseItem<TreeItemData>[]
): string[] {
  if (!items) return [];

  const stack: TreeViewBaseItem<TreeItemData>[] = [...items];
  const ids: string[] = [];

  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.hasMatchedChildren) {
      ids.push(node.id);
    }

    if (node.children) {
      stack.push(...node.children);
    }
  }

  return ids.filter((i) => !!i);
}

function traverse(
  node: TreeViewBaseItem<TreeItemData>,
  query: string,
  lowerQuery: string
): TreeViewBaseItem<TreeItemData> & {
  matched: boolean;
  hasMatchedChildren: boolean;
} {
  // Pre-compute lowercase strings only once
  const lowerLabel = node.label.toLowerCase();
  const lowerMetadataSubtitle = node.metadata?.subtitle?.toLowerCase();

  const isMatch =
    lowerLabel.includes(lowerQuery) ||
    !!lowerMetadataSubtitle?.includes(lowerQuery);

  const matchedChildren: TreeViewBaseItem<TreeItemData>[] = [];
  let hasMatchedChildren = false;

  // Process child nodes if they exist
  if (node.children) {
    for (const child of node.children) {
      const result = traverse(child, query, lowerQuery);
      if (result.matched || result.hasMatchedChildren) {
        matchedChildren.push(result);
        hasMatchedChildren = true;
      }
    }
  }

  return {
    ...node,
    children:
      isMatch && !hasMatchedChildren
        ? node.children
        : matchedChildren.length
        ? matchedChildren
        : undefined,
    matched: isMatch,
    hasMatchedChildren,
  };
}

function filterTreeItemsInternal(
  items: TreeViewBaseItem<TreeItemData>[],
  search: string,
  lowerQuery: string
): TreeViewBaseItem<TreeItemData>[] | undefined {
  const filtered: (TreeViewBaseItem<TreeItemData> & {
    matched: boolean;
    hasMatchedChildren: boolean;
  })[] = [];

  // Single pass: combine map, filter, and transform in one loop
  for (const root of items) {
    const traversed = traverse(root, search, lowerQuery);

    // Only process if matched or has matched children
    if (traversed.matched || traversed.hasMatchedChildren) {
      filtered.push({
        ...traversed,
        children:
          traversed.matched && !traversed.hasMatchedChildren
            ? traversed.children
            : traversed.children
            ? filterTreeItemsInternal(traversed.children, search, lowerQuery)
            : undefined,
      });
    }
  }

  return filtered;
}

function filterFlatTreeItems(
  items: TreeViewBaseItem<TreeItemData>[],
  search: string,
  limit?: number
): TreeViewBaseItem<TreeItemData>[] | undefined {
  const lowerQuery = search.toLowerCase();

  const filtered: TreeViewBaseItem<TreeItemData>[] = [];
  const limitCount = limit ? { count: 0, limit } : undefined;

  // Single pass with early exit for flat lists
  for (const item of items) {
    // Early exit if limit reached
    if (limitCount && limitCount.count >= limitCount.limit) {
      break;
    }

    // Simple string matching - no recursion needed
    const lowerLabel = item.label.toLowerCase();
    const lowerMetadataSubtitle = item.metadata?.subtitle?.toLowerCase();

    const isMatch =
      lowerLabel.includes(lowerQuery) ||
      !!lowerMetadataSubtitle?.includes(lowerQuery);

    if (isMatch) {
      filtered.push(item);
      if (limitCount) {
        limitCount.count++;
      }
    }
  }

  return filtered.length > 0 ? filtered : undefined;
}

export function filterTreeItems(
  items?: TreeViewBaseItem<TreeItemData>[],
  search?: string,
  isSearchQueryReachedMinimumLengthThreshold?: boolean,
  limit?: number
): TreeViewBaseItem<TreeItemData>[] | undefined {
  if (!search || !isSearchQueryReachedMinimumLengthThreshold) return items;
  if (!items || items.length === 0) return items;

  // Quick check: if no items have children, use optimized flat filtering
  const hasAnyChildren = items.some(
    (item) => item.children && item.children.length > 0
  );

  if (!hasAnyChildren) {
    // Use optimized flat list filtering with limit support
    return filterFlatTreeItems(items, search, limit);
  }

  // Use tree traversal for items with children (no limit support)
  const lowerQuery = search.toLowerCase();
  return filterTreeItemsInternal(items, search, lowerQuery);
}

export function calculateLabelMaxWidth(
  item: TreeViewBaseItem<TreeItemData>,
  metadataEnabled?: boolean,
  multiSelect?: boolean
) {
  const maxWidth = 234;
  let reduction = 0;
  if (metadataEnabled) {
    reduction += 30;
  }
  if (multiSelect) {
    reduction += 20;
  }
  if (item.children) {
    reduction += 20;
  }
  if (item.metadata?.comingSoon) {
    reduction += 30;
  }
  if (item.metadata?.logo_url) {
    reduction += 28;
  }
  if (item.metadata?.icon) {
    reduction += 20;
  }
  if (item.metadata?.secondaryIcon) {
    reduction += 18;
  }
  if (item.metadata?.records || item.metadata?.records === 0) {
    reduction += 30;
  }
  if (item.parentValue) {
    reduction += 20;
  }

  return `${maxWidth - reduction}px`;
}
